import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import useTranslate from '../../../../Hooks/useTranslate';
import { CenteredFlexBox } from '../../../FlexBox';
import StatTile from '../../charts/StatTile';
import DonutBreakdown from '../../charts/DonutBreakdown';
import ReportCard from '../../charts/ReportCard';
import NoData from './NoData';
import { pct, outcomeLabel } from './reportUtils';

/**
 * MobileInboundReport — inbound calls (getPivotData, direction 1): total /
 * answered / unanswered / unique-unanswered KPI tiles from branchCounters, plus
 * an outcome donut aggregated from the raw pivotData. See MOBILE_PLAN.md §F(9).
 */
const MobileInboundReport = function MobileInboundReport({
  inbound = null,
  allOutcomes = [],
}) {
  const theme = useTheme();
  const intl = useIntl();
  const translate = useTranslate();
  const mode = theme.palette.mode;

  const stats = useMemo(() => {
    const counters = inbound?.branchCounters || [];
    const totalCalls = counters.reduce((a, c) => a + (c.callCount || 0), 0);
    const unanswered = counters.reduce(
      (a, c) => a + (c.unansweredCallCount || 0),
      0
    );
    const uniqueUnanswered = counters.reduce(
      (a, c) => a + (c.uniqueUnansweredNumbersCount || 0),
      0
    );
    return {
      totalCalls,
      unanswered,
      answered: Math.max(0, totalCalls - unanswered),
      uniqueUnanswered,
    };
  }, [inbound]);

  const outcome = useMemo(() => {
    const rows = inbound?.pivotData || [];
    const byKey = {};
    rows.forEach((r) => {
      const key = r.callOutcome;
      if (key === null || key === undefined || key === '') return;
      byKey[key] = (byKey[key] || 0) + (r.callCount || 0);
    });
    const entries = Object.entries(byKey).filter(([, v]) => v > 0);
    const total = entries.reduce((a, [, v]) => a + v, 0);
    const labels = entries.map(([k]) => {
      const match = (allOutcomes || []).find((o) => String(o.id) === String(k));
      return match
        ? outcomeLabel(intl, match.name)
        : outcomeLabel(intl, String(k));
    });
    const colors = theme.custom.generateConsistentUniqueColors(labels);
    return {
      total,
      items: entries
        .map(([, v], i) => ({
          label: labels[i],
          value: pct(v, total),
          color: colors[i]?.[mode] ?? colors[i],
        }))
        .sort((a, b) => b.value - a.value),
    };
  }, [inbound, allOutcomes]);

  return (
    <>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('INBOUND_CALLS')}
          value={stats.totalCalls.toLocaleString()}
        />
        <StatTile
          label={translate('ANSWERED')}
          value={stats.answered.toLocaleString()}
          valueColor="success.main"
        />
      </CenteredFlexBox>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('UNANSWERED')}
          value={stats.unanswered.toLocaleString()}
          valueColor="error.main"
        />
        <StatTile
          label={translate('UNIQUE_UNANSWERED')}
          value={stats.uniqueUnanswered.toLocaleString()}
        />
      </CenteredFlexBox>
      <ReportCard title={translate('CALL_OUTCOMES')}>
        {outcome.items.length ? (
          <DonutBreakdown
            items={outcome.items}
            centerValue={outcome.total.toLocaleString()}
            centerLabel={translate('TOTAL')}
          />
        ) : (
          <NoData />
        )}
      </ReportCard>
    </>
  );
};

export default MobileInboundReport;
